/**
 * /admin/tiers — Staff-only "Manage user tiers" surface.
 *
 * Lookup a user by email, see their current tier + comp status, then flip
 * either via the same POST /api/auth/me/tier endpoint the Stripe webhook
 * and test fixtures already use.
 *
 * Designed for ongoing support: comp grants to family/friends/employees,
 * post-refund downgrades, in-flight tier swaps when Stripe is being weird.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, Navigate } from 'react-router-dom';
import { ArrowLeft, Search, Shield, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { useAuth } from '../contexts/AuthContext';
import { isOwner, isStaffOrAbove } from '../lib/tier';
import TierPill from '../components/TierPill';
import PageTitle from '../components/PageTitle';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Static label/value list. Titanium's `disabled` is resolved at runtime
// from the public `titanium_live` feature flag — fetched once per page
// mount in the component body below.
const buildTierOptions = (titaniumLive) => [
  { value: 'free', label: 'Ember (free)' },
  { value: 'pro', label: 'Steel (£4/mo)' },
  { value: 'pro_plus', label: 'Damascus (£7/mo)' },
  {
    value: 'pro_premium',
    label: titaniumLive ? 'Titanium (£10/mo)' : 'Titanium (£10/mo) — coming soon',
    disabled: !titaniumLive,
  },
];

const AdminTiers = () => {
  const { user } = useAuth();
  const isStaff = isStaffOrAbove(user);

  const [email, setEmail] = useState('');
  const [target, setTarget] = useState(null);
  const [tierChoice, setTierChoice] = useState('pro_plus');
  const [isComped, setIsComped] = useState(true);
  const [compExpiresAt, setCompExpiresAt] = useState(''); // 'YYYY-MM-DD' or '' for lifetime
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [titaniumLive, setTitaniumLive] = useState(false);

  // Drive the Titanium dropdown row's disabled state off the live DB flag
  // — flipping `titanium_live` in /admin/feature-flags makes Titanium
  // selectable here without a page reload.
  useEffect(() => {
    axios.get(`${API}/feature-flags`).then((r) => {
      setTitaniumLive(!!r.data?.titanium_live);
    }).catch(() => {
      // If the flag read fails, keep Titanium locked — fail-closed.
      setTitaniumLive(false);
    });
  }, []);

  const TIER_OPTIONS = buildTierOptions(titaniumLive);

  if (!isStaff) return <Navigate to="/home" replace />;

  const lookup = async (e) => {
    if (e) e.preventDefault();
    const needle = email.trim().toLowerCase();
    if (!needle || !needle.includes('@')) {
      toast.error('Enter a valid email.');
      return;
    }
    setSearching(true);
    setTarget(null);
    try {
      const r = await axios.get(`${API}/auth/users/lookup`, {
        params: { email: needle },
        withCredentials: true,
      });
      setTarget(r.data);
      setTierChoice(r.data.tier || 'free');
      setIsComped(!!r.data.is_comped);
      // Display existing expiry as YYYY-MM-DD; empty string == lifetime.
      const raw = r.data.comp_expires_at || '';
      setCompExpiresAt(raw ? raw.slice(0, 10) : '');
    } catch (err) {
      const msg = err.response?.status === 404
        ? 'No user with that email.'
        : err.response?.data?.detail || 'Lookup failed.';
      toast.error(msg);
    } finally {
      setSearching(false);
    }
  };

  const save = async () => {
    if (!target) return;
    setSaving(true);
    try {
      const r = await axios.post(
        `${API}/auth/me/tier`,
        {
          tier: tierChoice,
          target_user_id: target.user_id,
          is_comped: isComped,
          // Empty string clears any existing expiry (becomes a lifetime comp);
          // otherwise we pin to 23:59:59 of the chosen date so the user has
          // the full day. Stored as UTC ISO to match the model.
          comp_expires_at: isComped && compExpiresAt
            ? `${compExpiresAt}T23:59:59+00:00`
            : '',
        },
        { withCredentials: true },
      );
      setTarget(r.data);
      const tierLabel = TIER_OPTIONS.find((o) => o.value === tierChoice)?.label || tierChoice;
      const verb = isComped && tierChoice !== 'free' ? 'comped' : 'set';
      toast.success(`${r.data.name} ${verb} to ${tierLabel}.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed.');
    } finally {
      setSaving(false);
    }
  };

  const compWarning = isComped && tierChoice === 'free';
  const targetIsOwner = isOwner(target);
  const currentUserIsOwner = isOwner(user);
  const lockedForOwner = targetIsOwner && !currentUserIsOwner;

  return (
    <div className="p-6 sm:p-8 max-w-3xl mx-auto" data-testid="admin-tiers-page">
      <Link to="/home" className="text-sm text-[#0052FF] inline-flex items-center gap-1 hover:underline mb-4">
        <ArrowLeft className="w-4 h-4" />
        Back to home
      </Link>
      <div className="flex items-center gap-3 mb-1">
        <PageTitle
          title="Manage user tiers"
          subtitle="Comp accounts for family, friends, employees — or fix tier-state issues after refunds. Stripe webhooks ignore users with the Comp flag on, so their tier won't get overwritten by future events."
          className="mb-0"
        />
      </div>

      <form onSubmit={lookup} className="mt-6 flex gap-2" data-testid="admin-tiers-lookup-form">
        <div className="flex-1">
          <Label htmlFor="tier-email">User email</Label>
          <Input
            id="tier-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="someone@example.com"
            className="mt-1.5"
            data-testid="admin-tiers-email-input"
            autoFocus
          />
        </div>
        <div className="flex items-end">
          <Button type="submit" disabled={searching} className="bg-[#0052FF] text-white hover:bg-[#003ECC]" data-testid="admin-tiers-lookup-button">
            <Search className="w-4 h-4 mr-2" />
            {searching ? 'Looking up…' : 'Look up'}
          </Button>
        </div>
      </form>

      {target && (
        <div className="mt-6 bg-white border border-[#E2E8F0] rounded-lg p-6" data-testid="admin-tiers-result">
          {lockedForOwner && (
            <div className="mb-4 bg-gradient-to-r from-[#FEF3C7] to-[#FCD34D]/40 border border-[#FCD34D] rounded-md px-3 py-2 text-sm text-[#78350F] flex items-start gap-2" data-testid="admin-tiers-owner-lock">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>
                <strong>Protected — Owner account.</strong> Only the Owner can edit this row.
                To change the Owner, update the <code>OWNER_EMAIL</code> env var.
              </span>
            </div>
          )}
          <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#64748B]">Target user</div>
              <div className="text-lg font-semibold text-[#020617]">{target.name}</div>
              <div className="text-sm text-[#64748B]">{target.email}</div>
            </div>
            <div className="flex items-center gap-2">
              <TierPill user={target} />
              {target.is_comped && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-[#ECFDF5] text-[#047857] border border-[#A7F3D0]" data-testid="admin-tiers-comp-badge">
                  Comped
                </span>
              )}
            </div>
          </div>

          <fieldset disabled={lockedForOwner} className={lockedForOwner ? 'opacity-60' : ''}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>Tier</Label>
              <Select value={tierChoice} onValueChange={setTierChoice}>
                <SelectTrigger className="mt-1.5" data-testid="admin-tiers-tier-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIER_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value} disabled={o.disabled}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="comp-toggle">Comp this account (permanent)</Label>
              <div className="mt-1.5 flex items-center gap-3 h-10">
                <Switch
                  id="comp-toggle"
                  checked={isComped}
                  onCheckedChange={setIsComped}
                  data-testid="admin-tiers-comp-toggle"
                />
                <span className="text-sm text-[#475569]">
                  {isComped ? 'Stripe webhooks will skip this user.' : 'Standard paid account — Stripe controls the tier.'}
                </span>
              </div>
            </div>
          </div>

          {isComped && (
            <div className="mt-4">
              <Label htmlFor="comp-expires">Comp expires</Label>
              <div className="mt-1.5 flex items-center gap-3 flex-wrap">
                <input
                  id="comp-expires"
                  type="date"
                  value={compExpiresAt}
                  min={new Date().toISOString().slice(0, 10)}
                  onChange={(e) => setCompExpiresAt(e.target.value)}
                  className="h-10 px-3 rounded-md border border-[#E2E8F0] text-sm text-[#020617] bg-white focus:outline-none focus:ring-2 focus:ring-[#0052FF]"
                  data-testid="admin-tiers-comp-expires-input"
                />
                {compExpiresAt && (
                  <button
                    type="button"
                    onClick={() => setCompExpiresAt('')}
                    className="text-xs text-[#0052FF] hover:underline"
                    data-testid="admin-tiers-clear-expiry"
                  >
                    Clear (lifetime)
                  </button>
                )}
                <span className="text-xs text-[#64748B]">
                  {compExpiresAt
                    ? `Reverts to Ember on ${compExpiresAt} (end of day UTC).`
                    : 'Lifetime comp — never auto-reverts. Toggle Comp off to revoke.'}
                </span>
              </div>
            </div>
          )}

          {compWarning && (
            <div className="mt-4 bg-[#FEF3C7] border border-[#FCD34D] rounded-md px-3 py-2 flex items-start gap-2 text-sm text-[#92400E]" data-testid="admin-tiers-comp-warning">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>"Comp" on the free tier doesn't do anything. Either pick Steel or Damascus, or turn Comp off.</span>
            </div>
          )}

          <div className="mt-5 flex gap-2">
            <Button
              type="button"
              onClick={save}
              disabled={saving || lockedForOwner}
              className="bg-[#0052FF] text-white hover:bg-[#003ECC]"
              data-testid="admin-tiers-save-button"
            >
              {saving ? 'Saving…' : 'Save changes'}
            </Button>
            <Button
              type="button"
              onClick={() => { setTarget(null); setEmail(''); }}
              className="bg-[#F1F5F9] text-[#020617] hover:bg-[#E2E8F0]"
              data-testid="admin-tiers-clear-button"
            >
              Look up another
            </Button>
          </div>
          </fieldset>
        </div>
      )}

      <p className="text-xs text-[#94A3B8] mt-6">
        Comp accounts are intended for staff, family, friends, and gift recipients. Use the audit trail in
        MongoDB (<code>users.is_comped</code>, <code>users.subscription_status='comp'</code>) for tracking — there is
        no separate ledger.
      </p>
    </div>
  );
};

export default AdminTiers;
